import React, { useEffect, useState } from "react"
import { Col, Modal, Row, Spinner, Table } from "reactstrap"
import { GetCompanyByID } from "helpers/backend_helper"

const Field = ({ label, value }) => (
  <div className="mb-2">
    <span className="text-muted">{label}: </span>
    <span className="fw-semibold">{value?.toString().trim() ? value : "-"}</span>
  </div>
)

const ListField = ({ label, values }) => (
  <Field label={label} value={values?.length ? values.join(", ") : ""} />
)

const RowsTable = ({ label, headers, columns, rows }) => {
  const filledRows = (rows || []).filter(row =>
    columns.some(col => row?.[col]?.toString().trim())
  )

  if (!filledRows.length) return <Field label={label} value="" />

  return (
    <div className="mb-3">
      <span className="text-muted">{label}:</span>
      <div className="table-responsive">
        <Table size="sm" className="mt-1 mb-0">
          <thead>
            <tr>
              {headers.map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filledRows.map((row, idx) => (
              <tr key={idx}>
                {columns.map(col => (
                  <td key={col}>{row[col] || "-"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  )
}

const SelfDisclosureModal = ({ modal, close, user }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState()

  useEffect(() => {
    if (modal && user?.companyID) loadData()
  }, [modal, user])

  const loadData = async () => {
    try {
      setLoading(true)
      setData()
      const company = await GetCompanyByID(user.companyID)
      let disclosure = company?.traderSelfDisclosure
      if (typeof disclosure === "string") disclosure = JSON.parse(disclosure)
      setData(disclosure)
    } catch (error) {
      console.log(error)
      alert("An unexpected error happened!")
    }
    setLoading(false)
  }

  return (
    <Modal isOpen={modal} toggle={close} size="lg">
      <div className="modal-header">
        <h5 className="modal-title mt-0">
          Trader Self-Disclosure - {user?.name}
        </h5>
        <button
          type="button"
          onClick={close}
          className="btn-close"
          aria-label="Close"
        ></button>
      </div>
      <div className="modal-body">
        {loading && (
          <div className="text-center my-4">
            <Spinner color="primary" />
          </div>
        )}
        {!loading && !data && (
          <p className="text-muted mb-0">
            No self-disclosure questionnaire has been submitted for this
            company.
          </p>
        )}
        {!loading && data && (
          <>
            <h6 className="card-title mb-3">1. Company Information</h6>
            <Field
              label="Additional locations / branches"
              value={data.additionalLocations}
            />
            <Row>
              <Col md="4">
                <Field
                  label="Total employees worldwide"
                  value={data.totalEmployees}
                />
              </Col>
              <Col md="4">
                <Field
                  label="Total warehouse space (m²)"
                  value={data.warehouseSpaceM2}
                />
              </Col>
              <Col md="4">
                <Field label="Storage capacity" value={data.storageCapacity} />
              </Col>
            </Row>

            <h6 className="card-title mt-4 mb-3">
              2. Revenue &amp; Business Volume
            </h6>
            <Field
              label="Total revenue last fiscal year (€)"
              value={data.totalRevenueLastYear}
            />
            <RowsTable
              label="Revenue by region"
              headers={["Region", "Revenue (€)", "Share (%)"]}
              columns={["region", "revenue", "share"]}
              rows={data.revenueByRegion}
            />
            <RowsTable
              label="Revenue by component category"
              headers={["Component Category", "Revenue (€)", "Share (%)"]}
              columns={["category", "revenue", "share"]}
              rows={data.revenueByCategory}
            />
            <RowsTable
              label="Top manufacturers by revenue share"
              headers={[
                "Manufacturer",
                "Share of Revenue (%)",
                "Franchised Partner",
              ]}
              columns={["manufacturer", "share", "franchised"]}
              rows={data.topManufacturers}
            />

            <h6 className="card-title mt-4 mb-3">3. Sourcing Profile</h6>
            <ListField label="Business focus" values={data.sourcingFocus} />
            <Field
              label="No franchise business (open-market only)"
              value={data.noFranchiseBusiness ? "Yes" : "No"}
            />
            {!data.noFranchiseBusiness && (
              <RowsTable
                label="Franchised manufacturers"
                headers={[
                  "Manufacturer",
                  "Contract Region / Territory",
                  "Partner Since (Year)",
                ]}
                columns={["manufacturer", "region", "since"]}
                rows={data.franchisedManufacturers}
              />
            )}
            <Row>
              <Col md="6">
                <Field
                  label="Number of active suppliers worldwide"
                  value={data.numberOfActiveSuppliers}
                />
              </Col>
              <Col md="6">
                <Field
                  label="Average lead time (days)"
                  value={data.averageLeadTimeDays}
                />
              </Col>
            </Row>

            <h6 className="card-title mt-4 mb-3">
              4. Quality Assurance &amp; Test Laboratories
            </h6>
            <Field label="Own test laboratory" value={data.ownTestLab} />
            {data.ownTestLab === "Yes" && (
              <>
                <Field label="Location(s)" value={data.testLabLocations} />
                <ListField
                  label="Testing services offered"
                  values={data.testingServices}
                />
                <Field
                  label="Other testing services"
                  value={data.otherTestingServices}
                />
              </>
            )}
            <Field
              label="External partner laboratories used"
              value={data.externalPartnerLabs}
            />
            {data.externalPartnerLabs === "Yes" && (
              <Field label="Which ones" value={data.externalPartnerLabsWhich} />
            )}
            <ListField label="Certifications" values={data.certifications} />
            <Field
              label="Other certifications"
              value={data.otherCertifications}
            />
            <Field
              label="Membership in ERAI and/or IDEA"
              value={data.eraiIdeaMembership}
            />
            <Field
              label="Scope of traceability"
              value={data.traceabilityScope}
            />

            <h6 className="card-title mt-4 mb-3">
              5. Compliance &amp; Sustainability
            </h6>
            <Field
              label="Anti-counterfeiting policy in place"
              value={data.antiCounterfeitingPolicy}
            />
            <Field
              label="Conflict minerals policy (CMRT) in place"
              value={data.conflictMineralsPolicy}
            />

            <h6 className="card-title mt-4 mb-3">
              6. Digitalization &amp; System Integration
            </h6>
            <Field label="ERP system in use" value={data.erpSystem} />
            <Field label="EDI connection possible" value={data.ediConnection} />
            <Field label="API interface available" value={data.apiInterface} />

            <h6 className="card-title mt-4 mb-3">7. Trading Terms</h6>
            <Row>
              <Col md="6">
                <Field
                  label="Standard payment terms"
                  value={data.standardPaymentTerms}
                />
              </Col>
              <Col md="6">
                <Field
                  label="Accepted payment methods"
                  value={data.acceptedPaymentMethods}
                />
              </Col>
              <Col md="6">
                <Field
                  label="Standard Incoterms"
                  value={data.standardIncoterms}
                />
              </Col>
              <Col md="6">
                <Field label="Minimum order quantity (MOQ)" value={data.moq} />
              </Col>
            </Row>

            <h6 className="card-title mt-4 mb-3">
              8. References &amp; Other Information
            </h6>
            <Field
              label="Reference customers"
              value={data.referenceCustomers}
            />
            <Field
              label="Industry association memberships"
              value={data.industryAssociations}
            />
            <Field
              label="Additional comments"
              value={data.additionalComments}
            />
          </>
        )}
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={close}>
          Close
        </button>
      </div>
    </Modal>
  )
}

export default SelfDisclosureModal
